import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Calendar,
  Ticket,
  ShoppingCart,
  TrendingUp,
  ArrowUpRight,
  ArrowDownRight,
  Clock,
  CheckCircle,
  XCircle,
} from 'lucide-react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { cn, formatCurrency, formatNumber, formatDate } from '@utils';
import api from '@services/api';
import { ProgressBar } from '../../components/ui/ProgressBar';

interface DashboardData {
  totalEvents: number;
  publishedEvents: number;
  totalOrders: number;
  totalRevenue: number;
  totalTickets: number;
  checkedIn: number;
  recentOrders: Array<{
    id: number;
    orderNumber: string;
    buyerName: string;
    totalAmount: number;
    status: string;
    paymentStatus: string;
    createdAt: string;
    event?: { id: number; name: string };
  }>;
  upcomingEvents: Array<{
    id: number;
    name: string;
    startDate: string;
    capacity: number;
    soldCount: number;
    featuredImage?: string;
  }>;
}

interface SalesData {
  dailySales: Array<{ date: string; orders: number; revenue: number }>;
  totalRevenue: number;
  totalOrders: number;
  averageOrderValue: number;
}

interface StatCardProps {
  title: string;
  value: string | number;
  icon: React.ElementType;
  color: 'primary' | 'success' | 'warning' | 'danger';
  delay?: number;
}

function StatCard({ title, value, icon: Icon, color, delay = 0 }: StatCardProps) {
  const colorClasses = {
    primary: 'bg-primary-50 text-primary-600 dark:bg-primary-900/20 dark:text-primary-400',
    success: 'bg-success-50 text-success-600 dark:bg-success-900/20 dark:text-success-400',
    warning: 'bg-warning-50 text-warning-600 dark:bg-warning-900/20 dark:text-warning-400',
    danger: 'bg-danger-50 text-danger-600 dark:bg-danger-900/20 dark:text-danger-400',
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay }}
      className="card p-6"
    >
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm font-medium text-gray-500 dark:text-gray-400">{title}</p>
          <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">{value}</p>
        </div>
        <div className={cn('w-12 h-12 rounded-xl flex items-center justify-center', colorClasses[color])}>
          <Icon className="w-6 h-6" />
        </div>
      </div>
    </motion.div>
  );
}

export default function DashboardPage() {
  const [dateRange, setDateRange] = useState('30');

  const { data: stats, isLoading } = useQuery({
    queryKey: ['dashboard-stats'],
    queryFn: () => api.get<{ data: DashboardData }>('/dashboard/stats'),
  });

  const { data: salesReport } = useQuery({
    queryKey: ['dashboard-sales', dateRange],
    queryFn: () => api.get<{ data: SalesData }>('/reports/sales', { days: dateRange }),
  });

  const dashData = stats?.data;
  const chartData = salesReport?.data?.dailySales?.map(day => ({
    date: formatDate(day.date, 'MMM d'),
    revenue: day.revenue || 0,
    orders: day.orders || 0,
  })) || [];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Dashboard</h1>
          <p className="text-gray-500 dark:text-gray-400 mt-1">
            Welcome back! Here's what's happening with your events.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <select
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value)}
            className="input py-2"
            aria-label="Filter by Date Range"
          >
            <option value="7">Last 7 days</option>
            <option value="30">Last 30 days</option>
            <option value="90">Last 90 days</option>
          </select>
          <Link to="/events/create" className="btn-primary">
            <Calendar className="w-4 h-4" />
            Create Event
          </Link>
        </div>
      </div>

      {/* Stats Grid */}
      {isLoading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {[1, 2, 3, 4].map(i => (
            <div key={i} className="card p-6 animate-pulse">
              <div className="h-4 bg-gray-200 rounded w-1/2 dark:bg-gray-700 mb-3"></div>
              <div className="h-8 bg-gray-200 rounded w-3/4 dark:bg-gray-700"></div>
            </div>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          <StatCard
            title="Total Revenue"
            value={formatCurrency(dashData?.totalRevenue || 0)}
            icon={TrendingUp}
            color="success"
            delay={0}
          />
          <StatCard
            title="Tickets Sold"
            value={formatNumber(dashData?.totalTickets || 0)}
            icon={Ticket}
            color="primary"
            delay={0.1}
          />
          <StatCard
            title="Total Orders"
            value={formatNumber(dashData?.totalOrders || 0)}
            icon={ShoppingCart}
            color="warning"
            delay={0.2}
          />
          <StatCard
            title="Active Events"
            value={formatNumber(dashData?.publishedEvents || 0)}
            icon={Calendar}
            color="danger"
            delay={0.3}
          />
        </div>
      )}

      {/* Sales Chart */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.4 }}
        className="card p-6"
      >
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Sales Overview</h3>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-primary-500"></div>
              <span className="text-sm text-gray-500 dark:text-gray-400">Revenue</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-success-500"></div>
              <span className="text-sm text-gray-500 dark:text-gray-400">Orders</span>
            </div>
          </div>
        </div>
        <div className="h-80">
          {chartData.length > 0 ? (
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={chartData}>
                <defs>
                  <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#3B82F6" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="colorOrders" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#10B981" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#10B981" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis dataKey="date" stroke="#9CA3AF" fontSize={12} />
                <YAxis stroke="#9CA3AF" fontSize={12} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#fff',
                    border: '1px solid #E5E7EB',
                    borderRadius: '8px',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                  }}
                />
                <Area type="monotone" dataKey="revenue" stroke="#3B82F6" fillOpacity={1} fill="url(#colorRevenue)" strokeWidth={2} />
                <Area type="monotone" dataKey="orders" stroke="#10B981" fillOpacity={1} fill="url(#colorOrders)" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-full text-gray-400">
              No sales data available for this period
            </div>
          )}
        </div>
      </motion.div>

      {/* Bottom Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Orders */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.6 }}
          className="card"
        >
          <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Recent Orders</h3>
            <Link to="/orders" className="text-sm text-primary-600 hover:text-primary-700 dark:text-primary-400">
              View all
            </Link>
          </div>
          <div className="divide-y divide-gray-200 dark:divide-gray-700">
            {(dashData?.recentOrders || []).length === 0 ? (
              <div className="p-8 text-center text-gray-400">No recent orders</div>
            ) : (
              dashData?.recentOrders.map((order) => (
                <Link key={order.id} to={`/orders/${order.id}`} className="p-4 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors block">
                  <div className="flex items-center gap-4">
                    <div className={cn(
                      'w-10 h-10 rounded-lg flex items-center justify-center',
                      order.status === 'completed' && 'bg-success-50 text-success-600 dark:bg-success-900/20',
                      order.status === 'pending' && 'bg-warning-50 text-warning-600 dark:bg-warning-900/20',
                      order.status === 'failed' && 'bg-danger-50 text-danger-600 dark:bg-danger-900/20'
                    )}>
                      {order.status === 'completed' && <CheckCircle className="w-5 h-5" />}
                      {order.status === 'pending' && <Clock className="w-5 h-5" />}
                      {order.status === 'failed' && <XCircle className="w-5 h-5" />}
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900 dark:text-white">{order.orderNumber}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">{order.buyerName || 'Guest'}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-medium text-gray-900 dark:text-white">{formatCurrency(order.totalAmount || 0)}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">{formatDate(order.createdAt)}</p>
                  </div>
                </Link>
              ))
            )}
          </div>
        </motion.div>

        {/* Upcoming Events */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.7 }}
          className="card"
        >
          <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Upcoming Events</h3>
            <Link to="/events" className="text-sm text-primary-600 hover:text-primary-700 dark:text-primary-400">
              View all
            </Link>
          </div>
          <div className="divide-y divide-gray-200 dark:divide-gray-700">
            <div className="divide-y divide-gray-200 dark:divide-gray-700">
              {(dashData?.upcomingEvents || []).length === 0 ? (
                <div className="p-8 text-center text-gray-400">No upcoming events</div>
              ) : (
                dashData?.upcomingEvents.map((event) => {
                  const soldCount = event.soldCount || 0;
                  const capacity = event.capacity || 1;
                  const pct = capacity > 0 ? Math.round((soldCount / capacity) * 100) : 0;

                  return (
                    <Link key={event.id} to={`/events/${event.id}`} className="p-4 flex items-center gap-4 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors block">
                      <div className="w-12 h-12 rounded-lg bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center text-primary-600 dark:text-primary-400">
                        <Calendar className="w-6 h-6" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{event.name}</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">{formatDate(event.startDate)}</p>
                      </div>
                      <div className="text-right">
                        <div className="flex items-center gap-2">
                          <div className="w-24">
                            <ProgressBar percentage={pct} />
                          </div>
                          <span className="text-xs text-gray-500 dark:text-gray-400">{pct}%</span>
                        </div>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          {soldCount}/{capacity} sold
                        </p>
                      </div>
                    </Link>
                  );
                })
              )}
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
